import React, { useState } from 'react'
import { DealDetail, SpreadsheetMappingType, TradingEntityAssignmentDetail } from 'api'
import { Locale, Translation } from 'locale'

import { flatMap, isEmpty, noop } from 'lodash'
import {
  SpreadsheetParseDataHandler,
  SpreadsheetParseCompleteHandler,
  SpreadsheetParseDialog
} from 'view/shared/upload/SpreadsheetParseDialog'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import {
  TradingEntityAssignmentSpreadsheetParseResult,
  parseTradingEntityAssignmentSpreadsheet,
  PendingTradingEntityAssignment,
  PendingTradingEntityAssignmentFields
} from './parseTradingEntityAssignmentSpreadsheet'
import { isNotNil } from 'utils/predicate'
import {
  TradingEntityAssignmentValidatedParseResult,
  validateTradingEntityAssignmentParseResult
} from './validateTradingEntityAssignmentParseResult'

import { PendingValidationPanel, ValidationPanelColDef } from '../../../upload/pendingValidationPanel'
import styled from '@emotion/styled'
import { SpreadsheetParseUploadAsideWrapper } from 'view/shared/upload/SpreadsheetParseDialogStyles'
import { getTradingEntityAssignmentTradingType } from 'model/dealUtils'

export const TradingEntityAssignmentValidationPanelWrapper = styled(SpreadsheetParseUploadAsideWrapper)`
  min-width: 500px;
  height: 300px;
`

const pendingTradingEntityAssignmentColumnDefinitions: ValidationPanelColDef<PendingTradingEntityAssignment>[] = [
  {
    headerLabel: Translation.TC_TRADING_ENTITY_SHORT_NAME,
    getter: assignment => assignment.buySideEntity,
    fieldName: PendingTradingEntityAssignmentFields.BUY_SIDE_ENTITY
  },
  {
    headerLabel: Translation.TC_NOTIONAL_SHORT,
    getter: assignment => assignment.notional,
    fieldName: PendingTradingEntityAssignmentFields.NOTIONAL,
    formatter: Locale.formatNumber
  },
  {
    headerLabel: Translation.TC_START_CONSIDERATION,
    getter: assignment => assignment.startConsideration,
    fieldName: PendingTradingEntityAssignmentFields.START_CONSIDERATION,
    formatter: Locale.formatNumber
  },
  {
    headerLabel: Translation.TC_BOOK,
    getter: assignment => assignment.initiatorBook,
    fieldName: PendingTradingEntityAssignmentFields.BOOK
  },
  {
    headerLabel: Translation.TC_STRATEGY,
    getter: assignment => assignment.initiatorStrategy,
    fieldName: PendingTradingEntityAssignmentFields.STRATEGY
  }
]

export interface TradingEntityAssignmentDialogProps extends ModalConsumerProps {
  assignmentDetails: TradingEntityAssignmentDetail[]
  dealDetail: DealDetail
  initialClipboard?: string
  initialFile?: File
  onClose?: () => unknown
  onComplete: (assignments: PendingTradingEntityAssignment[]) => Promise<void>
}

export const TradingEntityAssignmentUploadDialogComponent: React.FC<TradingEntityAssignmentDialogProps> = ({
  onClose = noop,
  assignmentDetails,
  dealDetail,
  initialClipboard,
  initialFile,
  onComplete
}) => {
  const [validationResults, setValidationResults] = useState<TradingEntityAssignmentValidatedParseResult[]>()

  const handleParseData: SpreadsheetParseDataHandler<TradingEntityAssignmentSpreadsheetParseResult> = async (
    rowData,
    filename,
    spreadsheetMapping
  ) => {
    const parseResult = parseTradingEntityAssignmentSpreadsheet({
      rowData,
      filename,
      spreadsheetMapping
    })

    const assignmentType = getTradingEntityAssignmentTradingType(dealDetail)

    setValidationResults(
      isEmpty(parseResult.issues)
        ? validateTradingEntityAssignmentParseResult({ assignmentDetails, assignmentType })(parseResult)
        : undefined
    )

    return parseResult
  }

  const handleComplete: SpreadsheetParseCompleteHandler<TradingEntityAssignmentSpreadsheetParseResult> = async () => {
    if (validationResults) {
      await onComplete(validationResults.map(({ item: { _id, ...item } }) => item))
      onClose()
    }
  }

  return (
    <SpreadsheetParseDialog<TradingEntityAssignmentSpreadsheetParseResult>
      clipboard={initialClipboard}
      uploadFile={initialFile}
      canUpload={() => isNotNil(validationResults) && isEmpty(flatMap(validationResults, r => r.validationIssues))}
      onParseData={handleParseData}
      onComplete={handleComplete}
      onClose={onClose}
      parseResultItemsGetter={parseResult => parseResult.parsedItems}
      spreadsheetMappingType={SpreadsheetMappingType.FUND_ASSIGNMENT}
      title={Locale.translate(Translation.TC_TRADING_ENTITY_ASSIGN_UPLOAD_TITLE)}
      translations={{
        resultSectionTitle: Translation.TC_TRADING_ENTITY_ASSIGN_UPLOAD_MAPPING_TITLE,
        emptyDropzoneNarrative: Translation.SC_UPLOAD_DROPZONE_NARRATIVE,
        parseSuccessMessage: Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_SUCCESS,
        parseErrorMessage: Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_ERROR,
        parseErrorNarrative: Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_ERROR_NARRATIVE
      }}>
      {isNotNil(validationResults) && (
        <TradingEntityAssignmentValidationPanelWrapper>
          <PendingValidationPanel
            getRowId={r => r._id}
            panelHeading={Locale.translate(Translation.TC_TRADING_ENTITY_ASSIGN_UPLOAD_PENDING)}
            validationResults={validationResults}
            columnDefinitions={pendingTradingEntityAssignmentColumnDefinitions}
          />
        </TradingEntityAssignmentValidationPanelWrapper>
      )}
    </SpreadsheetParseDialog>
  )
}

export const TradingEntityAssignmentUploadDialog = withModal(TradingEntityAssignmentUploadDialogComponent)
